/**
 * Assessment Storage
 *
 * Manages persistence for assessment blueprints, attempts, and reports.
 * Delegates to dataLayer for all storage operations (localStorage / future backend).
 *
 * DDD co-locatie: moved from components/assessment/utils/ in s59.
 * dataLayer migration: D3 (8 april 2026).
 */

import {
  AssessmentBlueprint,
  AssessmentAttempt,
  AssessmentReport,
  AssessmentHistoryEntry,
  AssessmentDomainId,
  CooldownResult,
} from '../types'
import { GroupId } from '../../config/systemConfig'
import { ASSESSMENT_COOLDOWN } from '../../config/constants'
import { dataLayer } from '../../data/dataLayer'
import { debug, error as logError } from '../../utils/logger'

// ── Blueprints ──────────────────────────────────────────────────────────────

export async function saveBlueprint(
  blueprint: AssessmentBlueprint,
): Promise<void> {
  try {
    await dataLayer.saveAssessmentBlueprint(blueprint.id, blueprint)
    debug(`[AssessmentStorage] Saved blueprint: ${blueprint.id}`)
  } catch (error) {
    logError('Failed to save blueprint:', error)
  }
}

export async function getBlueprint(
  id: string,
): Promise<AssessmentBlueprint | null> {
  try {
    const result =
      await dataLayer.getAssessmentBlueprint<AssessmentBlueprint>(id)
    return result ?? null
  } catch (error) {
    logError('Failed to load blueprint:', error)
    return null
  }
}

export async function getAllBlueprints(): Promise<AssessmentBlueprint[]> {
  try {
    const all =
      await dataLayer.getAllAssessmentBlueprints<AssessmentBlueprint>()
    return Object.values(all).sort((a, b) => b.createdAt - a.createdAt)
  } catch (error) {
    logError('Failed to load blueprints:', error)
    return []
  }
}

// ── Attempts ────────────────────────────────────────────────────────────────

export async function saveAttempt(attempt: AssessmentAttempt): Promise<void> {
  try {
    await dataLayer.saveAssessmentAttempt(attempt.id, attempt)
    debug(`[AssessmentStorage] Saved attempt: ${attempt.id}`)
  } catch (error) {
    logError('Failed to save attempt:', error)
  }
}

export async function getAttempt(
  id: string,
): Promise<AssessmentAttempt | null> {
  try {
    const result = await dataLayer.getAssessmentAttempt<AssessmentAttempt>(id)
    return result ?? null
  } catch (error) {
    logError('Failed to load attempt:', error)
    return null
  }
}

export async function getAllAttempts(): Promise<AssessmentAttempt[]> {
  try {
    const all = await dataLayer.getAllAssessmentAttempts<AssessmentAttempt>()
    return Object.values(all).sort((a, b) => b.startedAt - a.startedAt)
  } catch (error) {
    logError('Failed to load attempts:', error)
    return []
  }
}

export async function getAttemptsByGroup(
  group: GroupId,
): Promise<AssessmentAttempt[]> {
  const all = await getAllAttempts()
  return all.filter((a) => a.selectedGroup === group)
}

// ── Reports ─────────────────────────────────────────────────────────────────

export async function saveReport(report: AssessmentReport): Promise<void> {
  try {
    await dataLayer.saveAssessmentReport(report.attemptId, report)
    debug(`[AssessmentStorage] Saved report: ${report.attemptId}`)
  } catch (error) {
    logError('Failed to save report:', error)
  }
}

export async function getReport(
  attemptId: string,
): Promise<AssessmentReport | null> {
  try {
    const result =
      await dataLayer.getAssessmentReport<AssessmentReport>(attemptId)
    return result ?? null
  } catch (error) {
    logError('Failed to load report:', error)
    return null
  }
}

export async function getAllReports(): Promise<AssessmentReport[]> {
  try {
    const all = await dataLayer.getAllAssessmentReports<AssessmentReport>()
    return Object.values(all).sort((a, b) => b.completedAt - a.completedAt)
  } catch (error) {
    logError('Failed to load reports:', error)
    return []
  }
}

export async function getReportsByGroup(
  group: GroupId,
): Promise<AssessmentReport[]> {
  const all = await getAllReports()
  return all.filter((r) => r.selectedGroup === group)
}

// ── History & Cooldown ──────────────────────────────────────────────────────

export async function getAssessmentHistory(
  group?: GroupId,
): Promise<AssessmentHistoryEntry[]> {
  const attempts = group
    ? await getAttemptsByGroup(group)
    : await getAllAttempts()
  return attempts
    .filter((a) => a.completedAt !== null)
    .map((a) => {
      const correctCount = a.answers.filter((ans) => ans.isCorrect).length
      const percentage =
        a.answers.length > 0
          ? Math.round((correctCount / a.answers.length) * 100)
          : 0
      return {
        attemptId: a.id,
        blueprintId: a.blueprintId,
        completedAt: a.completedAt ?? Date.now(),
        selectedGroup: a.selectedGroup,
        score: percentage,
        totalQuestions: a.answers.length,
        totalTimeSeconds: a.totalTimeSeconds,
        domainId: a.domainId,
        assignmentId: a.assignmentId,
        learnerId: a.learnerId,
      }
    })
    .sort((a, b) => b.completedAt - a.completedAt)
}

// ── Delete & Clear ──────────────────────────────────────────────────────────

export async function deleteAttempt(id: string): Promise<void> {
  try {
    await dataLayer.deleteAssessmentAttempt(id)
    debug(`[AssessmentStorage] Deleted attempt and report: ${id}`)
  } catch (error) {
    logError('Failed to delete attempt:', error)
  }
}

export async function clearAllAssessmentData(): Promise<void> {
  try {
    await dataLayer.clearAllAssessmentData()
    debug('[AssessmentStorage] Cleared all assessment data')
  } catch (error) {
    logError('Failed to clear assessment data:', error)
  }
}

// ── Cooldown ────────────────────────────────────────────────────────────────

/** Get the timestamp of the last completed assessment for a specific group. */
export async function getLastAssessmentTimestamp(
  group: GroupId,
): Promise<number> {
  const attempts = await getAttemptsByGroup(group)
  const completed = attempts.filter(
    (a) => a.status === 'completed' && a.completedAt !== null,
  )
  if (completed.length === 0) return 0
  return Math.max(...completed.map((a) => a.completedAt ?? 0))
}

/** Check if the assessment cooldown has elapsed for a specific group. */
export async function checkAssessmentCooldown(
  group: GroupId,
): Promise<CooldownResult> {
  const lastCompleted = await getLastAssessmentTimestamp(group)
  if (lastCompleted === 0)
    return { canStart: true, remainingMs: 0, lastCompletedAt: 0 }
  const elapsed = Date.now() - lastCompleted
  const remaining = ASSESSMENT_COOLDOWN.MIN_INTERVAL_MS - elapsed
  return {
    canStart: remaining <= 0,
    remainingMs: Math.max(0, remaining),
    lastCompletedAt: lastCompleted,
  }
}

/** Check cooldown per domain. Old attempts without domainId count for ALL domains. */
export async function checkDomainCooldown(
  group: GroupId,
  domainId: AssessmentDomainId,
): Promise<CooldownResult> {
  const attempts = await getAttemptsByGroup(group)
  const completed = attempts.filter(
    (a) =>
      a.status === 'completed' &&
      a.completedAt !== null &&
      // Match this domain OR legacy attempts without domainId (conservative)
      (a.domainId === domainId || !a.domainId),
  )
  if (completed.length === 0)
    return { canStart: true, remainingMs: 0, lastCompletedAt: 0 }
  const lastCompleted = Math.max(...completed.map((a) => a.completedAt ?? 0))
  const elapsed = Date.now() - lastCompleted
  const remaining = ASSESSMENT_COOLDOWN.MIN_INTERVAL_MS - elapsed
  return {
    canStart: remaining <= 0,
    remainingMs: Math.max(0, remaining),
    lastCompletedAt: lastCompleted,
  }
}
